import styles from './CmsMainTeaser.module.scss';
import CallToActionCard from '@components/components/CmsComponents/components/CmsMainTeaser/CallToActionCard/CallToActionCard';
import { trackInteraction } from '@components/hooks';
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  Button,
  Container,
  Content,
  FontAwesomeProIcon,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Video,
  getHeadingTag,
} from '@wienenergiegithub/ui-next';

const CmsMainTeaser = ({
  headingLevel = 3,
  background,
  ctaTitle,
  heading,
  image,
  interactionId,
  link,
  listItems,
  subheading,
  type,
  video,
}) => {
  const isReversed =
    background === 'right-blue' || background === 'right-orange';

  return (
    <Container
      className={classNames(styles.container, {
        [styles.reversed]: isReversed,
        [styles.fullBlueBackground]: type === 'cta',
      })}
      size={type === 'cta' ? 'lg' : 'full'}
    >
      <div
        className={classNames(styles.ImageWrapper, {
          [styles.rightOrange]: background === 'right-orange' && type !== 'cta',
          [styles.rightBlue]: background === 'right-blue' && type !== 'cta',
          [styles.leftOrange]: background === 'left-orange' && type !== 'cta',
          [styles.leftBlue]: background === 'left-blue' && type !== 'cta',
        })}
      >
        {type === 'cta' && (
          <CallToActionCard
            ctaTitle={ctaTitle}
            headingLevel={headingLevel + 1}
            image={image}
            interactionId={interactionId}
            link={link}
          />
        )}
        {type === 'image' && image && (
          <Image
            alt={image.title}
            className={classNames(styles.image, {
              [styles.reversed]: isReversed,
            })}
            height={image.height}
            href={link?.url}
            sizes={image.sizes}
            src={image.src}
            srcSet={image.srcSet}
            style={{ aspectRatio: `${image.width} / ${image.height}` }}
            target={link?.target}
            title={image.title}
            width={image.width}
          />
        )}
        {type === 'video' && video && video.video && (
          <Video
            autoPlay={video.autoplay_enabled}
            className={styles.video}
            controls={video.show_controls}
            height={video.video.height}
            loop={video.loop_enabled}
            muted={video.is_muted}
            poster={video.poster}
            preload="none"
            style={{
              aspectRatio: `${video.video.width} / ${video.video.height}`,
            }}
            title={video.title}
            track={video.track}
            video={video.video}
            width={video.video.width}
          />
        )}
      </div>
      <div
        className={classNames(styles.cardWrapper, {
          [styles.reversed]: isReversed,
          [styles.ctaCardText]: type === 'cta',
        })}
      >
        <Heading
          as={getHeadingTag(headingLevel)}
          size="h3"
          theme={type === 'cta' ? 'contrast' : 'default'}
        >
          {heading}
        </Heading>
        <Content theme={type === 'cta' ? 'contrast' : 'default'} withParser>
          {subheading}
        </Content>
        {listItems?.length > 0 && (
          <List
            className={classNames({ [styles.list]: type === 'cta' })}
            icon={
              <FontAwesomeProIcon
                icon="check"
                role="presentation"
                type="regular"
              />
            }
            type="checkmark"
            variant={type === 'cta' ? 'contrast' : 'success'}
          >
            {listItems?.map(({ id, text }) => (
              <ListItem key={id}>{text}</ListItem>
            ))}
          </List>
        )}
        {!!link && type !== 'cta' && (
          <Button
            className={styles.button}
            isElevated
            onClick={() => {
              trackInteraction(interactionId);
            }}
            render={<Link target={link.target} to={link.url} />}
          >
            {link.title}
          </Button>
        )}
      </div>
    </Container>
  );
};

CmsMainTeaser.size = 'full';

CmsMainTeaser.propTypes = {
  background: PropTypes.oneOf([
    'left-blue',
    'left-orange',
    'right-blue',
    'right-orange',
  ]),
  ctaTitle: PropTypes.string,
  heading: PropTypes.string.isRequired,
  headingLevel: PropTypes.number,
  image: PropTypes.shape({
    title: PropTypes.string,
    alt: PropTypes.string,
    sizes: PropTypes.string,
    src: PropTypes.string,
    srcSet: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    caption: PropTypes.string,
    height: PropTypes.number,
    width: PropTypes.number,
  }),
  interactionId: PropTypes.string,
  link: PropTypes.shape({
    title: PropTypes.string,
    url: PropTypes.string,
    target: PropTypes.string,
  }),
  listItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      id: PropTypes.string,
    }),
  ),
  subheading: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['image', 'video', 'cta']).isRequired,
  video: PropTypes.shape({
    autoplay_enabled: PropTypes.bool,
    src: PropTypes.string,
    srcSet: PropTypes.string,
    is_muted: PropTypes.bool,
    loop_enabled: PropTypes.bool,
    poster: PropTypes.string,
    show_controls: PropTypes.bool,
    title: PropTypes.string,
    track: PropTypes.string,
    video: PropTypes.shape({
      height: PropTypes.number,
      name: PropTypes.string,
      src: PropTypes.string,
      width: PropTypes.number,
    }),
  }),
};

export default CmsMainTeaser;
